import { saveAs } from 'file-saver'
import React, { useCallback, useMemo } from 'react'

import { Link, useNavigate } from 'react-router-dom'

import { useDispatch } from 'react-redux'

// UI
import { NonFormUpload } from '@/components/Elements'
import { Box, Button, Flex, HStack, Heading, Spacer } from '@chakra-ui/react'
import { MdDeleteOutline, MdOutlineCheckCircle } from 'react-icons/md'
import { toast } from 'react-toastify'

import { numberWithCommas } from '@/utils/stringHelper'

import { hideLoadingOverlay, showLoadingOverlay } from '@/stores/ui/loadingOverlaySlice'
import { createExcelBlob } from '@/utils/fileHelper'

import { useInitialTableState } from '@/components/Table/components/useInitialTableState'
import FullTable from '@/components/Table/FullTable/FullTable'
import { convertTableStateToQuery } from '@/utils/tableHelper'
import { createColumnHelper } from '@tanstack/react-table'

import { useRule } from '@/features/rules/useRule'
import { FilterType } from '@/components/Table/components/Filter/TableFilter'
import { LoadingSection } from '@/components/Elements'

const RuleListPage = () => {
  // Table State
  const [storedTableState, setStoredTableState] = useInitialTableState(`table_state:rule`, {})

  const query = convertTableStateToQuery(storedTableState)

  // Hooks
  const { useFetchAllRules, useRemoveRule, useUpdateRule, useUploadRules } = useRule()

  const navigate = useNavigate()
  const dispatch = useDispatch()
  const columnHelper = createColumnHelper()

  const uploadRules = useUploadRules()
  const updateRule = useUpdateRule()
  const removeRule = useRemoveRule()
  const { data: rulesResponse, isLoading } = useFetchAllRules(query)
  const { data: allRuleResponse } = useFetchAllRules({ page: 1, pageSize: 0 }) // Page Size 0 means get all

  const handleUpload = async (file) => {
    if (!file) {
      toast.error('No File Uploaded')
      return
    }

    try {
      dispatch(showLoadingOverlay())

      await uploadRules.mutateAsync(file)
    } catch (err) {
      console.log(err)
    } finally {
      dispatch(hideLoadingOverlay())
    }
  }

  const handleOnRowClick = (row) => {
    const rule = row?.original
    if (rule?._id) {
      navigate(`/admin/rules/${rule._id}/`)
    }
  }

  const handleDelete = useCallback(
    async (id) => {
      await removeRule.mutateAsync(id)
    },
    [removeRule]
  )

  const handleActivate = useCallback(
    async (id) => {
      try {
        dispatch(showLoadingOverlay())
        await updateRule.mutateAsync({ id, data: { isDeleted: false } })
      } catch (err) {
        console.log(err)
      } finally {
        dispatch(hideLoadingOverlay())
      }
    },
    [dispatch, updateRule]
  )

  const handleDownload = () => {
    if (!allRuleResponse?.data) return
    try {
      const excelData = allRuleResponse.data.map((rule) => {
        return {
          name: rule.name,
          offerType: rule.offerType,
          type: rule.type,
          currency: rule.currency,
          requiredBoth: rule.requiredBoth,
          isHighRisk: rule.isHighRisk,
          commercialApproverLevel: rule.commercialApproverLevel,
          technicalApproverLevel: rule.technicalApproverLevel,
          isSiemensStandardTerms: rule.isSiemensStandardTerms,
          hasUpperLimit: rule.hasUpperLimit,
          hasLowerLimit: rule.hasLowerLimit,
          upperLimit: rule.upperLimit,
          lowerLimit: rule.lowerLimit,
          status: rule.status,
        }
      })
      const excelBlob = createExcelBlob(excelData)
      saveAs(excelBlob, 'Rules.xlsx')
    } catch (err) {
      console.log(err)
    }
  }
  const defaultColumns = useMemo(
    () => [
      columnHelper.display({
        id: 'action',
        size: 100,
        enableHiding: false,
        enableColumnFilter: false,
        cell: ({ row: { original } }) => {
          return (
            <>
              {original?.status === 'active' && (
                <Flex _hover={{ cursor: 'pointer' }} color="gray.400" justifyContent={'center'} w="100%">
                  <MdDeleteOutline
                    size={24}
                    onClick={(e) => {
                      e.stopPropagation()
                      handleDelete(original._id)
                    }}
                  />
                </Flex>
              )}
              {original?.status === 'inactive' && (
                <Flex _hover={{ cursor: 'pointer' }} color="green.400" justifyContent={'center'} w="100%">
                  <MdOutlineCheckCircle
                    size={24}
                    onClick={(e) => {
                      e.stopPropagation()
                      handleActivate(original._id)
                    }}
                  />
                </Flex>
              )}
            </>
          )
        },
      }),

      columnHelper.accessor('name'),
      columnHelper.accessor('offerType', {
        meta: {
          filterVariant: FilterType.MULTI_SELECT,
          filterOptions: [
            { label: 'Maintenance', value: 'maintenance' },
            { label: 'Project', value: 'project' },
          ],
        },
      }),
      columnHelper.accessor('type', {
        meta: {
          filterVariant: FilterType.MULTI_SELECT,
          filterOptions: [
            { label: 'Gross Margin', value: 'grossMargin' },
            { label: 'Price', value: 'Price' },
            { label: 'Annual Price', value: 'annualPrice' },
            { label: 'Liability', value: 'liability' },
            { label: 'VO', value: 'VO' },
          ],
        },
      }),
      columnHelper.accessor('lowerLimit', {
        enableColumnFilter: false,
        cell: ({ getValue, row: { original } }) => {
          return <span>{!original?.hasLowerLimit ? '-' : numberWithCommas(getValue())}</span>
        },
      }),
      columnHelper.accessor('upperLimit', {
        enableColumnFilter: false,
        cell: ({ getValue, row: { original } }) => {
          return <span>{!original?.hasLowerLimit ? '-' : numberWithCommas(getValue())}</span>
        },
      }),
      // columnHelper.accessor('creator', {}),

      columnHelper.accessor('status', {
        meta: {
          filterVariant: FilterType.MULTI_SELECT,
          filterOptions: [
            { label: 'Active', value: 'active' },
            { label: 'Inactive', value: 'inactive' },
          ],
        },
      }),
    ],
    [columnHelper, handleActivate, handleDelete]
  )

  if (isLoading) {
    return <LoadingSection />
  }

  return (
    <>
      <Box shadow="md" borderWidth="1px" bg="white" w="100%" mb="auto">
        <Flex p={5} borderBottom="1px solid #d8dbe0">
          <Heading>Rules</Heading>

          <HStack mx="5">
            <Button colorScheme={'gray'} onClick={handleDownload}>
              Download
            </Button>

            <NonFormUpload uploadHandler={handleUpload} />
          </HStack>

          <Spacer />
          <Link to="/admin/rules/create">
            <Button colorScheme="blue">Create Rule</Button>
          </Link>
        </Flex>
        <Box p={5} overflow="scroll">
          <FullTable
            data={rulesResponse?.data ? rulesResponse.data : []}
            rowCount={rulesResponse?.count}
            columnDef={defaultColumns}
            state={storedTableState}
            setState={setStoredTableState}
            handleRowClick={handleOnRowClick}
          />
        </Box>
      </Box>
    </>
  )
}

export default RuleListPage
